import { IconType } from 'components/Icon';
import Pill from 'components/Widget/Pill';
import { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { validEmail } from 'utils';
import * as S from './styles';

interface EmailChipInputProps {
	chips: string[];
	onChipsChange: (chips: string[]) => void;
	suggestions: string[];
	disabled?: boolean;
	placeholder?: string;
}

const EmailChipInput = ({
	chips,
	onChipsChange,
	suggestions,
	disabled,
	placeholder = 'Add people by email',
}: EmailChipInputProps) => {
	const theme = useTheme();
	const inputRef = useRef<HTMLInputElement>(null);
	const [inputValue, setInputValue] = useState('');
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [activeSuggestion, setActiveSuggestion] = useState(0);

	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------

	// suggestions matching the current input, excluding emails already chipped
	const filteredSuggestions = useMemo(() => {
		const v = inputValue.trim().toLowerCase();
		const chipSet = new Set(chips.map((c) => c.toLowerCase()));
		return suggestions
			.filter((email) => !chipSet.has(email.toLowerCase()))
			.filter((email) => (v ? email.toLowerCase().includes(v) : true))
			.slice(0, 8);
	}, [suggestions, inputValue, chips]);

	const commitChip = (raw: string) => {
		const value = raw.trim().replace(/[,;]+$/, '');
		if (!value) return;
		if (chips.some((c) => c.toLowerCase() === value.toLowerCase())) {
			setInputValue('');
			return;
		}
		onChipsChange([...chips, value]);
		setInputValue('');
		setActiveSuggestion(0);
	};

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (showSuggestions && filteredSuggestions[activeSuggestion]) {
				commitChip(filteredSuggestions[activeSuggestion]);
			} else if (inputValue.trim()) {
				commitChip(inputValue);
			}
		} else if (e.key === ',' || e.key === ' ' || e.key === 'Tab') {
			if (inputValue.trim()) {
				e.preventDefault();
				commitChip(inputValue);
			}
		} else if (e.key === 'Backspace' && !inputValue && chips.length > 0) {
			onChipsChange(chips.slice(0, -1));
		} else if (e.key === 'ArrowDown') {
			if (filteredSuggestions.length > 0) {
				e.preventDefault();
				setShowSuggestions(true);
				setActiveSuggestion((i) => Math.min(i + 1, filteredSuggestions.length - 1));
			}
		} else if (e.key === 'ArrowUp') {
			if (filteredSuggestions.length > 0) {
				e.preventDefault();
				setActiveSuggestion((i) => Math.max(i - 1, 0));
			}
		} else if (e.key === 'Escape') {
			setShowSuggestions(false);
		}
	};

	const onRemoveChip = (email: string) => {
		onChipsChange(chips.filter((c) => c !== email));
	};

	const onPickSuggestion = (email: string) => {
		commitChip(email);
		inputRef.current?.focus();
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const dropdownVisible = showSuggestions && filteredSuggestions.length > 0 && !disabled;

	return (
		<S.Base>
			<S.ChipRow onClick={() => inputRef.current?.focus()}>
				{chips.map((email) => {
					const invalid = !validEmail(email);
					return (
						<Pill
							key={email}
							label={email}
							variant="default"
							iconLeft={invalid ? IconType.BANG : undefined}
							iconLeftColor={invalid ? theme.orange[700] : undefined}
							tooltip={invalid ? 'Not a valid email address' : undefined}
							onRemove={() => {
								if (disabled) return;
								onRemoveChip(email);
							}}
							noTruncate
						/>
					);
				})}
				<S.Input
					ref={inputRef}
					type="text"
					value={inputValue}
					placeholder={chips.length === 0 ? placeholder : ''}
					disabled={disabled}
					onChange={(e) => {
						setInputValue(e.target.value);
						setShowSuggestions(true);
						setActiveSuggestion(0);
					}}
					onFocus={() => setShowSuggestions(true)}
					onBlur={() => {
						// delay so a suggestion click can fire first
						setTimeout(() => setShowSuggestions(false), 150);
					}}
					onKeyDown={onKeyDown}
				/>
			</S.ChipRow>
			{dropdownVisible && (
				<S.Suggestions>
					{filteredSuggestions.map((email, i) => (
						<S.SuggestionItem
							key={email}
							$active={i === activeSuggestion}
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => onPickSuggestion(email)}
						>
							{email}
						</S.SuggestionItem>
					))}
				</S.Suggestions>
			)}
		</S.Base>
	);
};

export default EmailChipInput;
